import { setCurrentPageNo } from "pages/ManageRoles/roleSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DeleteIcon, EditIcon, TableDatePicker } from "themes/Icon";
import ButtonComp from "themes/elements/Button";
import DatePickerComp from "themes/elements/DatePicker";
import EmptyState from "themes/elements/EmptyState";
import PaginationComp from "themes/elements/Pagination";
import RangeFilter from "themes/elements/RangeFilter";
import SearchComp from "themes/elements/Search";
import SelectComp from "themes/elements/Select";
import TagComp from "themes/elements/Tag";
import TooltipComp from "themes/elements/Tooltip";
import { isPermit } from "utils/permissionCheck";
import "../table_comp.scss";

function RolesTable({
  filterUserCount,
  setFilterUserCount = () => {},
  column,
  dataClick,
  data,
  onDeleteAction,
  handleEdit,
  handleDuplicate = () => {},
  setSelectedData = () => {},
  isLoading,
  editAction,
  filterDesc,
  filterRole,
  filterStatus,
  filterCreatedDate,
  setFilterDesc = () => {},
  setFilterCreatedDate = () => {},
  setFilterRole = () => {},
  setFilterStatus = () => {},
}) {
  const dispatch = useDispatch();
  const [currentPageData, setCurrentPageData] = useState([]);
  const { totalPageNo, currentPageNo } = useSelector((state) => state.roles);
  const { rolesData, rolesDataDropdown } = useSelector((state) => state.roles);
  const [tempRange, setTempRange] = useState([0, 100]); // Temporary range for UI updates
  const [range, setRange] = useState(null); // Stores selected range

  useEffect(() => {
    setCurrentPageData(data);
    setSelectedData([]);
  }, [data]);

  const handlePageChange = (page) => {
    dispatch(setCurrentPageNo(page));
  };

  const handleCreatedDateChange = (dateString) => {
    dispatch(setCurrentPageNo(1));
    if (dateString) {
      setFilterCreatedDate(dateString);
    } else {
      setFilterCreatedDate(null);
    }
  };

  const handleStatusChange = (value, item) => {
    dispatch(setCurrentPageNo(1));
    setFilterStatus(item);
  };

  const onDataClick = (row) => {
    dataClick(row);
  };

  const handleSearchChange = (event) => {
    dispatch(setCurrentPageNo(1));
    const value = event.target.value;
    setFilterDesc(value);
  };

  const handleRoleChange = (value) => {
    dispatch(setCurrentPageNo(1));
    setFilterRole(value);
  };

  const SkeletonRow = ({ columns }) => (
    <tr className="table-row">
      {columns.map((col, index) => (
        <td key={index} className="table-data" style={{ width: col.width }}>
          <div className="skeleton-box"></div>
        </td>
      ))}
    </tr>
  );

  const clearFilters = () => {
    setCurrentPageData(data);
    setFilterDesc("");
    setRange(null);
    setFilterUserCount(null);
    setTempRange([0, 100]);
    setFilterRole(null);
    setFilterCreatedDate(null);
    dispatch(setCurrentPageNo(1));
    setFilterStatus({ value: 2, name: "All" });
  };

  return (
    <div className="table-wrapper">
      <table className="table-comp">
        <thead className="table-head">
          <tr className="table-row">
            {column.map((col, i) => (
              <th
                className="table-header"
                key={i}
                style={{ minWidth: col.width }}
              >
                <span className="flex align-center gap-3">{col.title}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-body">
          <tr className="table-row">
            {column.map((col, i) => (
              <td className="table-data" style={{ width: col.width }} key={i}>
                {col.filter === "searchDesc" ? (
                  <SearchComp
                    className="table-search"
                    height={33}
                    bgNone
                    placeholder="Search..."
                    value={filterDesc}
                    onChange={(e) => handleSearchChange(e)}
                  />
                ) : col.filter === "selectRole" ? (
                  <SelectComp
                    className="table-select"
                    value={filterRole}
                    placeholder={col.title}
                    onDeleteAction
                    onChange={handleRoleChange}
                    data={rolesDataDropdown?.map((item) => ({
                      label: item?.role_name,
                      value: Number(item?.role_id),
                    }))}
                    onClear={() => {
                      setFilterRole(null);
                      dispatch(setCurrentPageNo(1));
                    }}
                  />
                ) : col.filter === "filterRange" ? (
                  <RangeFilter
                    filterUserCount={filterUserCount}
                    range={range}
                    setRange={setRange}
                    setTempRange={setTempRange}
                    tempRange={tempRange}
                    setFilterUserCount={setFilterUserCount}
                  />
                ) : col.filter === "datePicker" ? (
                  <DatePickerComp
                    className="table-date-picker"
                    suffixIcon={<TableDatePicker />}
                    onChange={handleCreatedDateChange}
                    value={filterCreatedDate}
                  />
                ) : col.filter === "selectStatus" ? (
                  <SelectComp
                    allowClear={false}
                    value={filterStatus}
                    className="table-select"
                    placeholder={col.placeholder ? col.placeholder : col.title}
                    onChange={handleStatusChange}
                    data={col.dropDown.map((item) => ({
                      label: item.name,
                      value: item.value,
                    }))}
                    onClear={() => {
                      setFilterStatus(null);
                      dispatch(setCurrentPageNo(1));
                    }}
                  />
                ) : null}
              </td>
            ))}
          </tr>
          {(filterDesc ||
            filterCreatedDate ||
            filterUserCount ||
            filterRole ||
            filterStatus?.value !== 2) && (
            <div className="clr-btn-container">
              <ButtonComp height={16} variant="link" onClick={clearFilters}>
                Clear filters
              </ButtonComp>
            </div>
          )}
          {!isLoading ? (
            currentPageData?.length > 0 ? (
              currentPageData.map((row) => {
                //
                return (
                  <tr className="table-row" key={row?.id}>
                    {column.map((col, colIndex) => (
                      <td
                        style={{ width: col.width }}
                        className="table-data"
                        key={colIndex}
                      >
                        {col.chip ? (
                          <div>
                            <TagComp
                              color={
                                row.role_status_flg === 1 ? "green" : "red"
                              }
                            >
                              {row.role_status_flg === 1
                                ? "Active"
                                : "Inactive"}
                            </TagComp>
                          </div>
                        ) : colIndex === 0 ? (
                          <div className="flex align-center">
                            <TooltipComp title={row[col.key]}>
                              <span
                                className="table-title ml-3"
                                onClick={() => {
                                  onDataClick(row);
                                }}
                              >
                                {row[col.key]}
                              </span>
                            </TooltipComp>
                          </div>
                        ) : (
                          <span className="flex align-center gap-2 roles-table-data">
                            {row[col.key.replace(" ", "")]}
                          </span>
                        )}
                        <div className="action-container">
                          {editAction && isPermit(1, "edit") && (
                            <ButtonComp
                              onClick={() => handleEdit(row)}
                              height={32}
                              rounded
                            >
                              <EditIcon />
                            </ButtonComp>
                          )}
                          {isPermit(1, "delete") && (
                            <ButtonComp
                              onClick={() => onDeleteAction(row)}
                              height={32}
                              rounded
                            >
                              <DeleteIcon />
                            </ButtonComp>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <EmptyState />
            )
          ) : (
            Array(10)
              .fill(0)
              .map((_, index) => <SkeletonRow key={index} columns={column} />)
          )}
        </tbody>
      </table>
      <PaginationComp
        currentPageNo={currentPageNo}
        total={totalPageNo}
        onChange={handlePageChange}
      />
    </div>
  );
}

export default RolesTable;
